import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPendingCheckout,
  clearPendingCheckout,
} from "@/hooks/usePendingPurchaseCheck";

const CHECK_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 10;

/** Invisible component that checks for pending purchases on app load and tab focus */
export function PendingPurchaseChecker() {
  const navigate = useNavigate();
  const pollingRef = useRef(false);
  const attemptsRef = useRef(0);

  const checkForCompletedOrder = useCallback(async () => {
    const pending = getPendingCheckout();
    if (!pending || pollingRef.current) return;
    if (window.location.pathname.includes("order-bekraftelse")) {
      clearPendingCheckout();
      return;
    }

    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    if (!projectId) return;

    pollingRef.current = true;
    attemptsRef.current++;

    try {
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/lookup-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: pending.email }),
        }
      );

      if (res.ok) {
        const data = await res.json();
        if (data.found && data.orderName) {
          clearPendingCheckout();
          attemptsRef.current = 0;
          pollingRef.current = false;
          navigate(
            `/order-bekraftelse?order=${encodeURIComponent(data.orderName)}`
          );
          return;
        }
      }
    } catch {
      // Will retry
    }

    pollingRef.current = false;
    if (attemptsRef.current >= MAX_ATTEMPTS) {
      clearPendingCheckout();
      attemptsRef.current = 0;
    }
  }, [navigate]);

  useEffect(() => {
    const timer = setTimeout(checkForCompletedOrder, 1500);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        checkForCompletedOrder();
        const t1 = setTimeout(checkForCompletedOrder, CHECK_INTERVAL_MS);
        const t2 = setTimeout(checkForCompletedOrder, CHECK_INTERVAL_MS * 2);
        const cleanup = () => {
          clearTimeout(t1);
          clearTimeout(t2);
        };
        document.addEventListener("visibilitychange", cleanup, { once: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [checkForCompletedOrder]);

  return null;
}